import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ArrowLeft, MapPin, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

const serviceLinks = [
  { href: "/residential-tank-cleaning-gurugram", label: "Residential Tank Cleaning" },
  { href: "/overhead-water-tank-cleaning-gurugram", label: "Overhead Tank Cleaning" },
  { href: "/underground-water-tank-cleaning-gurugram", label: "Underground Tank Cleaning" },
  { href: "/commercial-water-tank-cleaning-gurugram", label: "Commercial Tank Cleaning" },
];

const nearbyAreas = [
  { href: "/water-tank-cleaning-dlf-phase-gurugram", label: "DLF Phase 1-5" },
  { href: "/water-tank-cleaning-sushant-lok-gurugram", label: "Sushant Lok" },
  { href: "/water-tank-cleaning-palam-vihar-gurugram", label: "Palam Vihar" },
  { href: "/water-tank-cleaning-golf-course-road-gurugram", label: "Golf Course Road" },
];

export default function Blog3() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Overhead vs Underground Tank Cleaning - What is Different? | TankBuddy</title>
        <meta name="description" content="Learn the key differences between overhead and underground water tank cleaning in Gurugram. TankBuddy explains the process. Call +91 98913 15777" />
        <link rel="canonical" href="https://www.tankbuddy.in/blog/overhead-vs-underground-tank-cleaning" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">

          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            TankBuddy Blog
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            Overhead vs Underground Tank Cleaning - What is Different?
          </h1>

          <div className="prose prose-slate max-w-none mb-10">
            <p className="text-slate-600 leading-relaxed text-base">
              Most Gurugram homes have both an overhead tank and an underground tank. But cleaning them requires completely different approaches, equipment and safety protocols. Understanding the difference helps you make the right choice for your home.
            </p>
            <p className="text-slate-600 leading-relaxed text-base mt-4">
              TankBuddy certified technicians are trained for both types of tank cleaning across all Gurugram sectors - from DLF villas with large underground reservoirs to high-rise apartments with rooftop overhead tanks.
            </p>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-10 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Book Tank Cleaning in Gurugram</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting Rs. 999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777">
                  <Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777
                </a>
              </Button>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Overhead Water Tank Cleaning
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Overhead tanks sit on rooftops or elevated platforms. They are exposed to direct sunlight, dust and bird droppings - making them prone to algae growth and contamination from external sources.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {[
                {step:"01", title:"Safe Rooftop Access", desc:"Technician climbs safely with proper equipment"},
                {step:"02", title:"Complete Draining", desc:"Tank is fully drained before cleaning begins"},
                {step:"03", title:"Jet Cleaning", desc:"High-pressure washing of all internal surfaces"},
                {step:"04", title:"Disinfection", desc:"Anti-bacterial spray and lid sealing"},
              ].map((item) => (
                <div key={item.step} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <span className="text-sky font-bold text-sm">Step {item.step}</span>
                  <h3 className="font-heading font-bold text-navy mt-1 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-sm">
              <strong>Time:</strong> 1-2 hours | <strong>Starting price:</strong> Rs. 999
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Underground Water Tank Cleaning
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Underground tanks are buried below ground level. They accumulate heavy sludge, sediment and are prone to bacterial contamination from soil seepage. Cleaning requires specialized equipment and strict safety protocols.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {[
                {step:"01", title:"Safety Assessment", desc:"Full inspection before any entry or work begins"},
                {step:"02", title:"Pump Dewatering", desc:"Complete removal of water using submersible pumps"},
                {step:"03", title:"Sludge Removal", desc:"Manual removal of accumulated sludge and sediment"},
                {step:"04", title:"Full Disinfection", desc:"Jet cleaning, sanitization and water quality test"},
              ].map((item) => (
                <div key={item.step} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <span className="text-sky font-bold text-sm">Step {item.step}</span>
                  <h3 className="font-heading font-bold text-navy mt-1 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-sm">
              <strong>Time:</strong> 2-4 hours | <strong>Starting price:</strong> Rs. 1,499
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Which Needs More Frequent Cleaning?
            </h2>
            <ul className="space-y-3">
              {[
                "Underground tanks need cleaning every 3-4 months due to higher contamination risk",
                "Overhead tanks can be cleaned every 6 months if properly covered",
                "Both should be cleaned before and after monsoon season",
                "Commercial tanks need quarterly cleaning with compliance certificate"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-sky/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-sky" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Can Both Be Cleaned Same Day?
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Yes! TankBuddy offers combo packages - get both your overhead and underground tanks cleaned in one visit at a discounted rate. This is the most cost-effective option for Gurugram homes with both tank types. Call us for a custom combo quote.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Our Services in Gurugram</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {serviceLinks.map(link => (
                <a key={link.href} href={link.href} className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-sky" />
                  </div>
                  <span className="text-sm font-medium text-navy">{link.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Also Serving Nearby Areas</h2>
            <div className="flex flex-wrap gap-2">
              {nearbyAreas.map(a => (
                <a key={a.href} href={a.href} className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors">
                  <MapPin className="w-3 h-3 text-sky" />{a.label}
                </a>
              ))}
            </div>
          </div>

        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
